import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import Switch from 'components/ui/switch';
import { Button } from 'components/ui/button';
import { useAuthContext } from 'modules/auth';
import * as Organization from 'modules/organization';
import { toast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import type { OrganizationPreferences as OrgPrefs } from '@examing/types';

const OrganizationPreferences: React.FC = () => {
    const { t } = useTranslation();
    const { state: authState } = useAuthContext();
    const orgId = authState.organization?.id;

    // Full settings bag is retained so a save merges (never drops) unrelated
    // free-form keys. Settings are not part of the auth profile, so the page
    // fetches the full org.
    const [settings, setSettings] = useState<Record<string, unknown>>({});
    const [prefs, setPrefs] = useState<OrgPrefs>(Organization.DEFAULT_ORG_PREFERENCES);
    const [baseline, setBaseline] = useState<OrgPrefs>(Organization.DEFAULT_ORG_PREFERENCES);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!orgId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        Organization.Api.GetById(orgId)
            .then(({ data }: any) => {
                const bag = (data?.settings ?? {}) as Record<string, unknown>;
                const resolved = Organization.resolveOrgPreferences(bag);
                setSettings(bag);
                setPrefs(resolved);
                setBaseline(resolved);
            })
            .catch(() => toast.error(t('failed_to_load_organization')))
            .finally(() => setIsLoading(false));
    }, [orgId, t]);

    const setAi = (key: keyof OrgPrefs['ai'], value: boolean) =>
        setPrefs((prev) => ({ ...prev, ai: { ...prev.ai, [key]: value } }));

    const setGrading = (key: keyof OrgPrefs['grading'], value: boolean) =>
        setPrefs((prev) => ({ ...prev, grading: { ...prev.grading, [key]: value } }));

    const isDirty = JSON.stringify(prefs) !== JSON.stringify(baseline);

    const handleSave = async () => {
        if (!orgId) return;
        setIsSaving(true);
        try {
            const nextSettings = { ...settings, ai: prefs.ai, grading: prefs.grading };
            await Organization.Api.Update(orgId, { settings: nextSettings });
            setSettings(nextSettings);
            setBaseline(prefs);
            toast.success(t('organization_updated_successfully'));
        } catch (err: any) {
            const message = err?.response?.data?.message || t('failed_to_load_organization');
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (<div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-muted-foreground"/>
      </div>);
    }

    return (<div className="w-full max-w-3xl space-y-8">
      {/* No page-level heading here — this page is only hosted inside the
          settings dialog, whose section title (SettingsDialog.tsx) is the
          single source of truth for the "Preferences" heading. The subtitle
          still carries context the dialog doesn't show, and no longer
          re-animates on every section switch. */}
      <p className="text-muted-foreground">{t("manage_ai_and_grading_preferences")}</p>

      {/* AI Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("ai_settings")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Switch checked={prefs.ai.gradingSuggestions} onChange={(v) => setAi('gradingSuggestions', v)} description={t("use_ai_to_assist_with_grading_short_answer_questions")}/>
            <Switch checked={prefs.ai.questionGeneration} onChange={(v) => setAi('questionGeneration', v)} description={t("allow_ai_to_generate_questions")}/>
          </div>
        </CardContent>
      </Card>

      {/* Grading */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("grading")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Switch checked={prefs.grading.anonymous} onChange={(v) => setGrading('anonymous', v)} description={t("hide_candidate_identity_while_grading")}/>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="outline" className="w-full sm:w-auto" disabled={!isDirty || isSaving} onClick={() => setPrefs(baseline)}>{t("cancel")}</Button>
        <Button className="w-full sm:w-auto" onClick={handleSave} disabled={!isDirty || isSaving}>
          {isSaving ? (<Loader2 size={14} className="mr-2 animate-spin"/>) : (<Save size={14} className="mr-2"/>)}{t("save_changes_2")}</Button>
      </div>
    </div>);
};
export default OrganizationPreferences;
